import { useMutation, useQuery } from "@tanstack/react-query";
import { History, Loader2, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";

export type VersionKind = "scoring_config" | "calibration_fixture";

interface VersionRecord {
  id: string;
  versionNumber: number;
  content: unknown;
  createdAt: string;
  createdBy: string | null;
  reason: string | null;
}

export interface VersionsPanelProps {
  kind: VersionKind;
  /** For scoring_config: section name. For calibration_fixture: the raw
   *  candidate_name. Prefer `fixtureId` for calibration_fixture so the PII
   *  candidate_name never has to leave the server. */
  identifier?: string;
  /** When set, the panel lists by fixture_id — the server resolves to
   *  candidate_name internally. Takes precedence over `identifier`. */
  fixtureId?: string;
  /** Called after a successful rollback so the caller can refetch the parent
   *  section's current data. */
  onRollback?: () => void;
  /** Shown as the dialog title — e.g., "Histórico · scoring:dim:tenure". */
  label?: string;
}

function formatContentPreview(content: unknown): string {
  if (content === null || content === undefined) return "(vazio)";
  if (typeof content === "string") return content;
  try {
    return JSON.stringify(content, null, 2);
  } catch {
    return String(content);
  }
}

export function VersionsPanel({
  kind,
  identifier,
  fixtureId,
  onRollback,
  label,
}: VersionsPanelProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const effectiveKey = fixtureId ? `fixture:${fixtureId}` : `${kind}:${identifier ?? ""}`;
  const queryKey = ["labs", "configVersions", effectiveKey];

  const { data: versions, isLoading } = useQuery<VersionRecord[]>({
    queryKey,
    enabled: open,
    queryFn: async () => {
      if (fixtureId) {
        return rpcClient.labs.configVersionsListByFixture({ id: fixtureId });
      }
      return rpcClient.labs.configVersionsList({
        kind,
        identifier: identifier ?? "",
        limit: 50,
      });
    },
  });

  const rollbackMutation = useMutation({
    mutationFn: async (versionId: string) => {
      await rpcClient.labs.configVersionsRollback({ id: versionId });
    },
    onSuccess: () => {
      toast({ title: "Versão restaurada" });
      queryClient.invalidateQueries({ queryKey });
      onRollback?.();
    },
    onError: (err: Error) => {
      toast({ title: "Falha ao restaurar", description: err.message, variant: "destructive" });
    },
  });

  const selected = versions?.find((v) => v.id === selectedId);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <History className="h-4 w-4" />
          Histórico
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[min(720px,90vw)] sm:max-w-none flex flex-col">
        <SheetHeader>
          <SheetTitle>{label ?? "Histórico de versões"}</SheetTitle>
          <SheetDescription>
            Cada alteração vira uma versão nova. Restaurar gera outra versão com o conteúdo antigo —
            nada é sobrescrito.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 grid grid-cols-[280px_1fr] gap-4 mt-4 min-h-0">
          <div className="border rounded-md h-full overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : !versions || versions.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                Nenhuma versão ainda. A próxima edição inaugura a lista.
              </div>
            ) : (
              <ul className="divide-y">
                {versions.map((v) => {
                  const isSelected = v.id === selectedId;
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(v.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-accent/50 transition-colors ${isSelected ? "bg-accent" : ""}`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="font-medium tabular-nums">v{v.versionNumber}</span>
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(v.createdAt).toLocaleString("pt-BR", {
                              dateStyle: "short",
                              timeStyle: "short",
                            })}
                          </span>
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                          {v.createdBy ?? "sistema"}
                          {v.reason ? ` · ${v.reason}` : ""}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex flex-col min-h-0">
            {selected ? (
              <>
                <div className="flex items-baseline justify-between pb-2 border-b">
                  <div>
                    <div className="font-medium">v{selected.versionNumber}</div>
                    <div className="text-xs text-muted-foreground">
                      {selected.createdBy ?? "sistema"} ·{" "}
                      {new Date(selected.createdAt).toLocaleString("pt-BR")}
                      {selected.reason ? ` · ${selected.reason}` : ""}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => rollbackMutation.mutate(selected.id)}
                    disabled={rollbackMutation.isPending}
                    data-testid="versions-rollback"
                  >
                    {rollbackMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4 mr-2" />
                    )}
                    Restaurar esta versão
                  </Button>
                </div>
                <div className="flex-1 mt-2 overflow-y-auto">
                  <pre className="text-xs font-mono whitespace-pre-wrap break-words p-2">
                    {formatContentPreview(selected.content)}
                  </pre>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center text-sm text-muted-foreground">
                Escolha uma versão à esquerda para visualizar.
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
